import { FilterTab } from "@/components/ui/FilterTabs";
import { UserData, UserStatus } from "@/entities/users/types";

export interface BulkActionConfig {
  showActivate: boolean;
  showDeactivate: boolean;
}

export interface UndoUserData {
  id: string;
  previousValue: string | undefined;
  previousRole?: string | number;
  previousDepartment?: string | number;
}

export type UndoAction = "reportingManager" | "role";

export interface UndoData {
  users: UndoUserData[];
  action: UndoAction;
}

export interface TransferData {
  companies: string | null;
  deals: string | null;
  leads: string | null;
  newReportingManager: string | null;
}

export interface RecordCount {
  companies: number;
  deals: number;
  leads: number;
  directReports: number;
}

export interface StatusCounts {
  all: number;
  active: number;
  inactive: number;
  pending: number;
  expired: number;
}

export interface UserFilters {
  status?: UserStatus[];
}

export interface SearchParams {
  term: string;
}

export interface AggregationItem {
  fieldValue: string;
  fields: Array<{
    aggregations?: {
      count: number;
    };
  }>;
}

export interface StatusAggregation {
  groupedBy: "status";
  data: AggregationItem[];
}

export interface UserSelectionHook {
  selectedUsers: Set<string>;
  selectAll: boolean;
  selectedUserData: UserData[];
  handleUserSelect: (userId: string, checked: boolean) => void;
  handleSelectAll: (checked: boolean) => void;
  clearSelection: () => void;
}

export interface UserManagementHook {
  searchTerm: string;
  activeFilter: string;
  selectedUsers: Set<string>;
  selectAll: boolean;
  users: UserData[] | undefined;
  selectedUserData: UserData[];
  filterTabs: FilterTab[];
  statusCounts: StatusCounts;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  sorting: any;
  setSearchTerm: (term: string) => void;
  setActiveFilter: (filter: string) => void;
  handleUserSelect: (userId: string, checked: boolean) => void;
  handleSelectAll: (checked: boolean) => void;
  clearSelection: () => void;
  onLoadMore: () => void;
  onSortingChange: (sorting: any) => void;
}

export const STATUS_FILTER_MAP: Record<string, UserStatus> = {
  active: UserStatus.ACTIVE,
  inactive: UserStatus.INACTIVE,
  pending: UserStatus.PENDING,
  expired: UserStatus.EXPIRED
} as const;

export const STATUS_VALUE_MAP: Record<string, keyof Omit<StatusCounts, "all">> =
  {
    [UserStatus.ACTIVE.toString()]: "active",
    [UserStatus.INACTIVE.toString()]: "inactive",
    [UserStatus.PENDING.toString()]: "pending",
    [UserStatus.EXPIRED.toString()]: "expired"
  } as const;

export const TRANSLATION_KEY = "settings.userManagement";
export const DEBOUNCE_DELAY = 300;
export const PAGE_SIZE = 50;
